import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { SapPriceCampaignBannerEntry, SapPriceCampaignDetails } from '../../../../../core/model';

const TIME_BETWEEN_SHIFT = 10;

interface BannerStatusItem {
  bannerId: string;
  active: boolean;
}
@Component({
  selector: 'py-campaign-banner-carousel',
  templateUrl: './campaign-banner-carousel.component.html',
  styleUrls: ['./campaign-banner-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignBannerCarouselComponent implements OnInit, OnDestroy {
  subscription: Subscription = new Subscription();

  activeBannerEntry: SapPriceCampaignBannerEntry;
  timeToNextBannerEntry: number = TIME_BETWEEN_SHIFT;
  isBannerEntryStopped: boolean = false;

  bannerStatusItems: Array<BannerStatusItem> = [];
  _priceCampaignDetails: SapPriceCampaignDetails;

  @Input() set priceCampaignDetails(campaignDetails: SapPriceCampaignDetails) {
    this._priceCampaignDetails = Object.assign({}, campaignDetails);
    if (campaignDetails) {
      this.activeBannerEntry = this.priceCampaignDetails.entries[0];
      this.initializeActiveBannerArray(this.priceCampaignDetails.entries);
    }
  }

  get priceCampaignDetails(): SapPriceCampaignDetails {
    return this._priceCampaignDetails;
  }

  @ViewChildren('progressBar') progressBars: QueryList<any>;

  constructor(private cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.subscription.add(
      interval(1000)
        .pipe(
          filter((_) => !!this.priceCampaignDetails),
          map((_) => {
            if (!this.isBannerEntryStopped) {
              if (this.timeToNextBannerEntry - 1 < 0) {
                this.timeToNextBannerEntry = TIME_BETWEEN_SHIFT;
                this.activateNextEntry();
                return this.timeToNextBannerEntry;
              }

              this.timeToNextBannerEntry -= 1;
              this.updateBarWidth(this.activeBannerEntry.id, this.timeToNextBannerEntry);

              return this.timeToNextBannerEntry;
            }
          })
        )
        .subscribe()
    );
  }

  selectActiveBannerEntryManually(index: number): void {
    this.resetBarWidth(this.activeBannerEntry.id);
    this.resetTime();
    this.selectActiveBannerEntry(index);
  }

  selectActiveBannerEntry(index: number): void {
    for (let i = 0; i < this.bannerStatusItems.length; i++) {
      if (i === index) {
        this.bannerStatusItems[i].active = true;
        this.activeBannerEntry = this.priceCampaignDetails.entries.find(
          (entry) => entry.id === this.bannerStatusItems[i].bannerId
        );
        this.cd.detectChanges();
      } else {
        this.bannerStatusItems[i].active = false;
      }
    }
  }

  changeState(): void {
    this.onSendBannerEntryState(!this.isBannerEntryStopped);
  }

  onSendBannerEntryState(isStopped: boolean): void {
    this.isBannerEntryStopped = isStopped;
  }

  private activateNextEntry(): void {
    const currentIndex = this.bannerStatusItems.findIndex((entry) => entry.active);
    const nextIndex = currentIndex >= 0 && currentIndex < this.bannerStatusItems.length - 1 ? currentIndex + 1 : 0;
    this.selectActiveBannerEntry(nextIndex);
  }

  private updateBarWidth(id: string, time: number): void {
    this.progressBars.toArray().forEach((bar) => {
      if (bar?.nativeElement && bar.nativeElement?.id === `bar-${id}`) {
        const width = 100 - (time / TIME_BETWEEN_SHIFT) * 100;
        this.setBarWidth(bar?.nativeElement, width);
      }
    });
  }

  private resetBarWidth(id: string): void {
    this.progressBars.toArray().forEach((bar) => {
      if (bar?.nativeElement && bar.nativeElement?.id === `bar-${id}`) {
        this.setBarWidth(bar?.nativeElement, 0);
      }
    });
  }

  private setBarWidth(progressBar: HTMLElement, width: number): void {
    progressBar.style.width = width + '%';
  }

  private resetTime(): void {
    this.timeToNextBannerEntry = TIME_BETWEEN_SHIFT;
  }

  private initializeActiveBannerArray(entries: SapPriceCampaignBannerEntry[]): void {
    this.bannerStatusItems = [];
    for (let i = 0; i < entries.length; i++) {
      const active = i === 0 ? true : false;
      this.bannerStatusItems.push({ bannerId: entries[i].id, active: active });
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
